/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import useStyles from '../../styles';
import nationalMenuData from '../../../common/pageSetup/categories/nationalMenuData';
import LinkCaret, { CaretDirection } from '../../../coreComponents/links/LinkCaret';
import { MINIMUM_DISPLAYED_ITEMS } from '../constants';
import NationalMenuTile from './NationalMenuTile';
import { openLocalizationRail } from '@/localization/actions';
import { onNationalClickGetStarted } from '@/dataAnalytics/dataAnalyticsHelper';
import { useCategoryDisplayNameContext } from '../context/CategoryDisplayNameProvider';
import router from '../../../router';
import { Paths } from '@/router/nextJsRouter';
import CategoryHeader from '../../../common/components/CategoryHeader';
import { NationalMenuTileData } from '@/common/pageSetup/categories/nationalMenuData/types';
import {
  createItemAnalyticsForNationalProduct,
  onNationalClickSeeMore,
  onNationalMenuTileClick,
  onNationalNonDealMenuPageLoad
} from '../categories.analytics';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import { useApplicationInitialized } from '@/hooks/useApplicationInitialized';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';

const NationalMenuPage = (): JSX.Element => {
  const classes = useStyles();
  const [expandList, setExpandList] = useState(false);
  const toggleSeeAll = () => setExpandList(!expandList);
  const dispatch = useDispatch();
  const menuTilesRef = useRef<HTMLLIElement[]>([]);
  const { categoryDisplayName, categoryID, loadingCategoryName } = useCategoryDisplayNameContext();
  const isAppInitialized = useApplicationInitialized();
  const analytics = useAnalytics();

  const handleLocalizationClick = (productName: string) => {
    analytics.push(() => onNationalMenuTileClick(categoryDisplayName, productName));
    dispatch(openLocalizationRail());
  };

  const handleCTAClick = () => {
    analytics.push(() => onNationalClickGetStarted(categoryDisplayName));
    dispatch(openLocalizationRail());
  };

  const handleToggle = () => {
    toggleSeeAll();
  };

  const nationalData = nationalMenuData(categoryID) as NationalMenuTileData[];

  const products = nationalData
    ?.sort((a, b) => a.priority - b.priority)
    .map((product, index) => {
      const isHidden = !expandList && index >= MINIMUM_DISPLAYED_ITEMS;
      return (
        <li
          key={product.id}
          ref={(element: HTMLLIElement) => {
            menuTilesRef.current[index] = element;
          }}
          className={clsx(classes.nationalMenuTile, isHidden && classes.hiddenListItem)}
          data-testid={`national-menu-tile-${product.id}`}
        >
          <NationalMenuTile
            {...product}
            image={{
              mobile: product.mobileImageUrl,
              desktop: product.desktopImageUrl,
              altText: product.name
            }}
            handleClick={() => handleLocalizationClick(product.name)}
          />
        </li>
      );
    });

  useEffect(() => {
    if (!products?.length) {
      router.goToRoute(Paths.HOME);
    }
  }, [products.length]);

  const shouldShowSeeMore = products.length > MINIMUM_DISPLAYED_ITEMS;

  useEffect(() => {
    if (expandList) {
      const firstHiddenItem = menuTilesRef.current[MINIMUM_DISPLAYED_ITEMS];
      const firstChild = firstHiddenItem?.firstChild as HTMLElement;
      firstChild.focus();
      analytics.push(() => onNationalClickSeeMore(categoryDisplayName));
    }
  }, [expandList]);

  useEffect(() => {
    if (isAppInitialized && !loadingCategoryName) {
      const categoryItemsAnalytics = createItemAnalyticsForNationalProduct(
        nationalData, categoryDisplayName
      );
      analytics.push(() => onNationalNonDealMenuPageLoad(categoryDisplayName,
        categoryItemsAnalytics));
    }
  }, [isAppInitialized, loadingCategoryName, categoryDisplayName]);

  return (
    <>
      {(products.length > 0) && (
        <Grid data-testid={`${categoryID}-national-menu-page`}>
          <CategoryHeader
            loadingCategoryName={loadingCategoryName}
            handleCTAClick={handleCTAClick}
            pageDisplayName={categoryDisplayName}
          />
          <Grid className={classes.content}>
            <ul className={classes.menu}>
              {products}
            </ul>
            {shouldShowSeeMore
              && (
                <Typography align="center">
                  <LinkCaret
                    text={expandList ? 'See less' : 'See more'}
                    ariaLabel={expandList ? `See less popular ${categoryDisplayName}` : `See more popular ${categoryDisplayName}`}
                    caretDirection={expandList ? CaretDirection.UP : CaretDirection.DOWN}
                    onClick={handleToggle}
                    tabIndex={0}
                  />
                </Typography>
              )}
            <CaloriesDisclaimer />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default NationalMenuPage;
